import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Settings, Lock, Edit2, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfileSettings({ user, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Username change
  const [newUsername, setNewUsername] = useState('');
  const [usernamePassword, setUsernamePassword] = useState('');
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleUsernameChange = async () => {
    if (!newUsername || newUsername.length < 3) {
      toast.error('Логин кемінде 3 таңбадан тұруы керек');
      return;
    }
    if (!usernamePassword) {
      toast.error('Құпия сөзді енгізіңіз');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/auth/change-username`, {
        new_username: newUsername,
        current_password: usernamePassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Логин сәтті өзгертілді! Қайта кіріңіз.');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Логинді өзгерту сәтсіз');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (!currentPassword) {
      toast.error('Ағымдағы құпия сөзді енгізіңіз');
      return;
    }
    if (!newPassword || newPassword.length < 4) {
      toast.error('Жаңа құпия сөз кемінде 4 таңбадан тұруы керек');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Жаңа құпия сөздер сәйкес келмейді');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Құпия сөз сәтті өзгертілді!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Құпия сөзді өзгерту сәтсіз');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="icon"
          className="bg-white/10 border-white/20 text-white hover:bg-white/20"
          title="Баптаулар"
        >
          <Settings className="w-4 h-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Аккаунт баптаулары
          </DialogTitle>
        </DialogHeader>
        
        <div className="bg-slate-100 dark:bg-slate-800 rounded-lg p-3 mb-4">
          <p className="text-sm">
            <span className="font-medium">Логин:</span> {user?.username}
          </p>
          <p className="text-sm">
            <span className="font-medium">Аты-жөні:</span> {user?.full_name}
          </p>
          <p className="text-sm">
            <span className="font-medium">Рөл:</span> {user?.role === 'admin' ? 'Admin' : user?.role === 'teacher' ? 'Мұғалім' : 'Оқушы'}
          </p>
        </div>
        
        <Tabs defaultValue="username" className="mt-2">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="username" className="flex items-center gap-1">
              <Edit2 className="w-4 h-4" />
              Логин
            </TabsTrigger>
            <TabsTrigger value="password" className="flex items-center gap-1">
              <Lock className="w-4 h-4" />
              Құпия сөз
            </TabsTrigger>
          </TabsList>
          
          {/* Username Tab */}
          <TabsContent value="username" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Логинді өзгерту</CardTitle>
                <CardDescription>Жаңа логин енгізіңіз</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="newUsername">Жаңа логин</Label>
                  <Input
                    id="newUsername"
                    value={newUsername}
                    onChange={(e) => setNewUsername(e.target.value)}
                    placeholder="Жаңа логин"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="usernamePassword">Ағымдағы құпия сөз</Label>
                  <div className="relative">
                    <Input
                      id="usernamePassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={usernamePassword}
                      onChange={(e) => setUsernamePassword(e.target.value)}
                      placeholder="Құпия сөзіңіз"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    ⚠️ Логинді өзгерткеннен кейін қайта кіру қажет
                  </p>
                </div>
                <Button 
                  onClick={handleUsernameChange} 
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Edit2 className="w-4 h-4 mr-2" />}
                  Логинді өзгерту
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
          
          {/* Password Tab */}
          <TabsContent value="password" className="space-y-4 mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Құпия сөзді өзгерту</CardTitle>
                <CardDescription>Жаңа құпия сөз орнатыңыз</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="currentPassword">Ағымдағы құпия сөз</Label>
                  <div className="relative">
                    <Input
                      id="currentPassword"
                      type={showCurrentPassword ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Ағымдағы құпия сөз"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="newPassword">Жаңа құпия сөз</Label>
                  <div className="relative">
                    <Input
                      id="newPassword"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Жаңа құпия сөз (кемінде 4 таңба)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Құпия сөзді растау</Label>
                  <Input
                    id="confirmPassword"
                    type={showNewPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Жаңа құпия сөзді қайталаңыз"
                  />
                </div>
                <Button 
                  onClick={handlePasswordChange} 
                  disabled={loading}
                  className="w-full"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Lock className="w-4 h-4 mr-2" />}
                  Құпия сөзді өзгерту
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
